import type { ListResponse, RunDetail, RunSummary } from "../../api/types";
import generatedSnapshots from "./contractReportSnapshots.generated.json";

export const CONTRACT_REPORT_SNAPSHOT_PROJECT_ID = "project_d2b0a9ad11664716a83a";
export const CONTRACT_REPORT_SNAPSHOT_RUN_IDS = [
  "AMCA-ACL_English_Contract_Engineer_Run_1_1",
  "AMCA-ACL_English_Contract_Engineer_Run_2_1",
  "AMCA-ACL_English_Contract_Engineer_Run_3_1",
  "AMC-FACL_English_Contract_Engineer_Run_1_1",
  "AMC-FACL_English_Contract_Engineer_Run_2_1",
  "AMC-FACL_English_Contract_Engineer_Run_3_1",
] as const;
const contractReportSnapshotRunIds = new Set<string>(CONTRACT_REPORT_SNAPSHOT_RUN_IDS);

export function isContractReportSnapshotRunId(runId: string): boolean {
  return contractReportSnapshotRunIds.has(runId);
}

export interface ContractReportSnapshot {
  summary: RunSummary;
  detail: RunDetail;
}

export interface ContractReportSnapshotDocument {
  schema_version: 1;
  project_id: typeof CONTRACT_REPORT_SNAPSHOT_PROJECT_ID;
  runs: ContractReportSnapshot[];
}

const snapshotDocument = generatedSnapshots as ContractReportSnapshotDocument;

function validSnapshots(document: ContractReportSnapshotDocument): ContractReportSnapshot[] {
  if (document.schema_version !== 1 || document.project_id !== CONTRACT_REPORT_SNAPSHOT_PROJECT_ID) return [];
  if (!document.runs.length) return [];
  const allowedRunIds = contractReportSnapshotRunIds;
  const documentRunIds = new Set(document.runs.map(({ detail }) => detail.run_id));
  if (document.runs.length !== allowedRunIds.size || documentRunIds.size !== allowedRunIds.size) return [];
  if ([...allowedRunIds].some((runId) => !documentRunIds.has(runId))) return [];
  if (document.runs.some(({ summary, detail }) => (
    !allowedRunIds.has(detail.run_id)
    || summary.run_id !== detail.run_id
    || summary.project_id !== CONTRACT_REPORT_SNAPSHOT_PROJECT_ID
    || detail.project_id !== CONTRACT_REPORT_SNAPSHOT_PROJECT_ID
    || String(summary.status ?? "").toLowerCase() !== "completed"
    || String(detail.status ?? "").toLowerCase() !== "completed"
  ))) return [];
  return document.runs;
}

export function contractReportSnapshotSummaries(
  projectId: string,
  document: ContractReportSnapshotDocument = snapshotDocument,
): RunSummary[] {
  if (projectId !== CONTRACT_REPORT_SNAPSHOT_PROJECT_ID) return [];
  return validSnapshots(document).map(({ summary }) => summary);
}

export function mergeContractReportSnapshotSummaries(
  projectId: string,
  remote: ListResponse<RunSummary>,
  document: ContractReportSnapshotDocument = snapshotDocument,
): ListResponse<RunSummary> {
  const snapshots = contractReportSnapshotSummaries(projectId, document);
  if (!snapshots.length) return remote;
  const snapshotRunIds = new Set(snapshots.map(({ run_id }) => run_id));
  return { ...remote, items: [...remote.items.filter(({ run_id }) => !snapshotRunIds.has(run_id)), ...snapshots] };
}

export function getContractReportSnapshot(
  projectId: string,
  runId: string,
  document: ContractReportSnapshotDocument = snapshotDocument,
): RunDetail | undefined {
  if (projectId !== CONTRACT_REPORT_SNAPSHOT_PROJECT_ID) return undefined;
  return validSnapshots(document).find(({ detail }) => detail.run_id === runId)?.detail;
}
